import { ChannelName, IncidentAlertAction, OrderStatus } from '@/enums';
import { useGetChannels } from '@/hooks';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import useRootOrg from '@/hooks/useRootOrg';
import { getAdminStyle } from '@/pages/authenPages/Administrator/AdminManagement';
import { getKitAlertSeverity } from '@/pages/authenPages/AlertQC/qc-alert/components/AlertOverview/utils';
import { useReportSupportKits } from '@/queries/kit.query';
import { useGetCountToProcessOrder } from '@/queries/order.query';
import { PRIVATE_ROUTES } from '@/routes/routes';
import { useGetRoutesWithPermissions } from '@/routes/useGetRoutesWithPermissions';
import { Icon, PhotoUser } from '@amili-v3/amili-v3-design';
import { icons } from '@amili-v3/amili-v3-design/src/icons/components';
import { Menu, MenuProps, Skeleton } from 'antd';
import classNames from 'classnames';
import qs from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuItem } from '../../types';
import FirstLoginPromptModal from '../commonComponents/FirstLoginPromptModal';
import SwitchLanguageButton from '../SwitchLanguageBtn';
import MenuItemWithBadgeLabel from './MenuItemWithBadgeLabel';
import { MenuWrapper, ProfileWrapper, SiderWrapper } from './styles';
import './styles.scss';

const getItem = (props: {
  label: React.ReactNode;
  key: React.Key | RegExp;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  type?: 'group';
  order?: number;
}): MenuItem & { order: number } => {
  const { key, icon, type, order, label, disabled, className } = props;
  return { key, icon, type, order, label, disabled, className } as MenuItem & {
    order: number;
  };
};

const getBasePath = (path: string) => {
  if (path.includes(PRIVATE_ROUTES.PROFILE.ROOT)) {
    return path.split('/').slice(0, 2).join('/');
  }
  return path.split('/').slice(0, 3).join('/');
};

const icon = (name: keyof typeof icons) => (
  <span className="menu-icon">
    <Icon name={name} color="var(--primary)" width={15} height={15} />
  </span>
);

const CLASS_NAME = 'admin-sider';

const Sider = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: loggedInUser, isLoading } = useGetMe();
  const { channels, isLoading: isLoadingChannel } = useGetChannels();

  const loggedInUserMainRole = loggedInUser?.mainRole;
  const { permissions } = useGetRoutesWithPermissions();

  const iconByRole = loggedInUserMainRole?.name
    ? getAdminStyle(loggedInUserMainRole.name)
    : undefined;

  const [page, setPage] = useState(pathname);
  const isRootOrg = useRootOrg();

  const handleClick: MenuProps['onClick'] = e => {
    if (e.key === PRIVATE_ROUTES.KIT.ROOT) {
      // Default kit tab: "all" for root orgs, Amili Health channel otherwise.
      return navigate({
        pathname: PRIVATE_ROUTES.KIT.ROOT,
        search:
          !isRootOrg && amiliHealthChannelId
            ? `?${qs.stringify({ channelId: amiliHealthChannelId })}`
            : undefined,
      });
    }

    return navigate(e.key);
  };

  const [newClassName, setNewClassName] = useState(classNames(CLASS_NAME));

  const {
    data: reportSupportKits = [],
    isLoading: isLoadingAlert,
    isRefetching: isRefetchAlert,
    isError: isErrorAlert,
  } = useReportSupportKits(
    { action: IncidentAlertAction.INVESTIGATE },
    {
      refetchInterval: 300000,
      enabled: !!permissions.ALERT,
      retry: false,
    },
  );

  const criticalQcAlertCount = useMemo(
    () => reportSupportKits.filter(kit => !!getKitAlertSeverity(kit)).length,
    [reportSupportKits],
  );

  const amiliHealthChannelId = channels.find(
    ch => ch.name === ChannelName.AMILI_HEALTH,
  )?.id;

  const {
    data: noOfToProcessOrder,
    isLoading: isLoadingNoOfToProcessOrder,
    isRefetching: isRefetchNoOfToProcessOrder,
    isError: isErrorNoOfToProcessOrder,
  } = useGetCountToProcessOrder({
    params: {
      channelIds: [amiliHealthChannelId as string],
      orderStatuses: [OrderStatus.CREATED],
    },
    options: {
      refetchInterval: 300000,
      enabled: !isLoadingChannel && !!amiliHealthChannelId,
      retry: false,
    },
  });

  const items: MenuProps['items'] = useMemo(() => {
    const base = [
      getItem({
        disabled: !permissions.PROFILE,
        className: 'profile',
        icon: (
          <span className="menu-icon">
            <PhotoUser
              primary
              width={30}
              height={30}
              iconWidth={16}
              iconHeight={16}
              className="icon-by-role"
              gradient={iconByRole?.gradient}
              icon={iconByRole?.icon as keyof typeof icons}
            />
          </span>
        ),
        label: (
          <div>
            {isLoading ? (
              <Skeleton.Button
                active
                size="large"
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <ProfileWrapper
                onClick={() => {
                  if (permissions.PROFILE) {
                    navigate(PRIVATE_ROUTES.PROFILE.ROOT);
                  }
                }}
              >
                <div className="user-general-info">
                  <span className="user-name">
                    {[loggedInUser?.firstName, loggedInUser?.lastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim()}
                  </span>
                  <span className="user-role">
                    {loggedInUserMainRole?.name}
                  </span>
                </div>
              </ProfileWrapper>
            )}
          </div>
        ),
        key: PRIVATE_ROUTES.PROFILE.ROOT,
        order: 1,
      }),
      permissions.ALERT && isRootOrg
        ? getItem({
            label: (
              <MenuItemWithBadgeLabel
                label={t('titles.alert')}
                backgroundColor="var(--error)"
                badgeNumber={criticalQcAlertCount}
                loading={(isLoadingAlert || isRefetchAlert) && !isErrorAlert}
              />
            ),
            key: PRIVATE_ROUTES.ALERT.ROOT,
            icon: icon('alert'),
            order: 1,
          })
        : null,
      permissions.DASHBOARD
        ? getItem({
            label: t('titles.dashboard'),
            key: PRIVATE_ROUTES.DASHBOARD,
            icon: icon('gridLayout'),
            order: 1,
          })
        : null,
      permissions.ADMINISTRATOR
        ? getItem({
            label: t('titles.administrator'),
            key: PRIVATE_ROUTES.ADMINISTRATOR.ROOT,
            icon: icon('userManagement'),
            order: 2,
          })
        : null,
      permissions.CUSTOMER
        ? getItem({
            label: t('titles.customer'),
            key: PRIVATE_ROUTES.CUSTOMER.ROOT,
            icon: icon('customerGroup'),
            order: 3,
          })
        : null,
      permissions.PROJECT
        ? getItem({
            label: t('titles.project'),
            key: PRIVATE_ROUTES.PROJECT.ROOT,
            icon: icon('folder'),
            order: 3,
          })
        : null,
      permissions.ORDER
        ? getItem({
            label: (
              <MenuItemWithBadgeLabel
                label={t('titles.order')}
                backgroundColor="var(--primary)"
                badgeNumber={noOfToProcessOrder}
                badgeTooltip={
                  t('order.createdOrdersBadgeTooltip', {
                    count: noOfToProcessOrder ?? 0,
                  }) as string
                }
                onClickBadge={() =>
                  navigate({
                    pathname: PRIVATE_ROUTES.ORDER.ROOT,
                    search: `?${qs.stringify({ orderStatus: OrderStatus.CREATED })}`,
                  })
                }
                loading={
                  (isLoadingNoOfToProcessOrder ||
                    isRefetchNoOfToProcessOrder) &&
                  !isErrorNoOfToProcessOrder
                }
              />
            ),
            key: PRIVATE_ROUTES.ORDER.ROOT,
            icon: icon('shoppingCard'),
            order: 4,
          })
        : null,
      permissions.KIT
        ? getItem({
            label: t('titles.kit'),
            key: PRIVATE_ROUTES.KIT.ROOT,
            icon: icon('package'),
            order: 5,
          })
        : null,
      permissions.EXTRACTION
        ? getItem({
            label: t('titles.extractionBatch'),
            key: PRIVATE_ROUTES.EXTRACTION.ROOT,
            icon: icon('testTube'),
            order: 6,
          })
        : null,
      permissions.SEQUENCING
        ? getItem({
            label: t('titles.sequencingBatch'),
            key: PRIVATE_ROUTES.SEQUENCING.ROOT,
            icon: icon('dna'),
            order: 7,
          })
        : null,
      permissions.ANALYSIS
        ? getItem({
            label: t('titles.analysis'),
            key: PRIVATE_ROUTES.ANALYSIS.ROOT,
            icon: icon('barChart'),
            order: 8,
          })
        : null,
      permissions.REPORT
        ? getItem({
            label: t('titles.report'),
            key: PRIVATE_ROUTES.REPORT.ROOT,
            icon: icon('fileText'),
            order: 9,
          })
        : null,
      permissions.FMT
        ? getItem({
            label: t('titles.FMT'),
            key: PRIVATE_ROUTES.FMT.ROOT,
            icon: icon('medicalCross'),
            order: 10,
          })
        : null,
      permissions.EMAIL
        ? getItem({
            label: t('common.email'),
            key: PRIVATE_ROUTES.EMAIL,
            icon: icon('mail'),
            order: 12,
          })
        : null,
      permissions.PRODUCT
        ? getItem({
            label: t('titles.product'),
            key: PRIVATE_ROUTES.PRODUCT.ROOT,
            icon: icon('inventory'),
            order: 13,
          })
        : null,
      permissions.ORGANIZATION
        ? getItem({
            label: t('titles.organization'),
            key: PRIVATE_ROUTES.ORGANIZATION.ROOT,
            icon: icon('home'),
            order: 14,
          })
        : null,
    ].sort((a, b) => {
      if (!a || !b) return 0;
      return a?.order - b?.order;
    });
    return base;
  }, [
    t,
    permissions,
    isRootOrg,
    noOfToProcessOrder,
    criticalQcAlertCount,
    iconByRole,
    navigate,
  ]);

  const handleScroll = useCallback(() => {
    const sider = document.querySelector(`.${CLASS_NAME}`);
    if (!sider) return;
    // 40 is the top & bottom sider margin
    const maxScroll = 67 + 40 - (window.innerHeight - sider.scrollHeight);
    setNewClassName(
      classNames(
        CLASS_NAME,
        { 'admin-sider__shadow-top': sider.scrollTop > 0 },
        {
          'admin-sider__shadow-bottom': Math.ceil(sider.scrollTop) < maxScroll,
        },
      ),
    );
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setPage(getBasePath(pathname));
  }, [pathname]);

  return (
    <>
      <SiderWrapper>
        <div className="profile">
          <Icon name={'logoLilac'} className="logo-area" />
          <SwitchLanguageButton />
        </div>

        <MenuWrapper>
          <Menu
            items={items}
            mode="inline"
            selectedKeys={[page]}
            onClick={handleClick}
            onScroll={handleScroll}
            className={newClassName}
          />
        </MenuWrapper>
      </SiderWrapper>
      <FirstLoginPromptModal />
    </>
  );
};

export default Sider;
